import {Button} from '@wandb/components';
import React, {useState} from 'react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import {GroupSelectionState} from '../../../util/selectionmanager';
import {InstrumentedCenteredWaveLoader} from '../../utility/InstrumentedLoader';
import {MoveRunDrawer} from './MoveRunDrawer';
// @todo: Migrate to 'Dialog.Drawer' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Body, Container, Drawer, Header} from './MoveRunDrawerPieces';
import {useTrackRunsWithLocation} from './moveRunsEvents';
import {useSelectedRuns} from './useSelectedRuns';

type DrawerLoadingProps = {
  isInReport: boolean;
  open: boolean;
  onClose: () => void;
};

/**
 * This component is rendered while we convert from the groupTempSelection to
 * the concrete list of runs we are about to move. It is intended to look like
 * the `MoveRunDrawer` so that going from loading->done isn't a jarring
 * transition.
 *
 * Ideally this component goes away when we refactor RunSelector, such that we
 * can more-easily track which runs are being selected in the client, making the
 * additional gql request redundant.
 */
function DrawerLoading({isInReport, open, onClose}: DrawerLoadingProps) {
  return (
    <Drawer open={open} onClose={onClose}>
      <Container isInReport={isInReport}>
        <Header onClose={onClose} title="Move runs to another group" />
        <Body>
          <InstrumentedCenteredWaveLoader name="load-all-groups" size="small" />
        </Body>
      </Container>
    </Drawer>
  );
}

type MoveRunDrawerBulkProps = {
  entityName: string;
  groupTempSelection: GroupSelectionState;
  onClose: () => void;
  onMovedRuns: () => void;
  open: boolean;
  projectName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  selectedCount: number;
};

function MoveRunDrawerBulk({
  entityName,
  groupTempSelection,
  onClose,
  onMovedRuns,
  open,
  projectName,
  groupSelectionsRef,
  selectedCount,
}: MoveRunDrawerBulkProps) {
  const isInReport = useIsInLoadedReportContext();
  const {runs, loading} = useSelectedRuns({
    entityName,
    projectName,
    groupTempSelection,
    skip: !open,
  });

  if (loading) {
    return (
      <DrawerLoading isInReport={isInReport} open={open} onClose={onClose} />
    );
  }
  return (
    <MoveRunDrawer
      entityName={entityName}
      onClose={onClose}
      onMovedRuns={onMovedRuns}
      open={open}
      projectName={projectName}
      runs={runs}
      groupSelectionsRef={groupSelectionsRef}
      selectedCount={selectedCount}
    />
  );
}

type MoveRunDrawerBulkButton = {
  entityName: string;
  groupTempSelection: GroupSelectionState;
  onMovedRuns: () => void;
  projectName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  selectedCount: number;
};
export function MoveRunDrawerBulkButton({
  entityName,
  groupTempSelection,
  onMovedRuns,
  projectName,
  groupSelectionsRef,
  selectedCount,
}: MoveRunDrawerBulkButton) {
  const [moveRunDrawerOpen, setMoveRunDrawerOpen] = useState(false);
  const trackMoveRunsWithLocation = useTrackRunsWithLocation();
  return (
    <>
      <MoveRunDrawerBulk
        entityName={entityName}
        groupTempSelection={groupTempSelection}
        onClose={() => setMoveRunDrawerOpen(false)}
        onMovedRuns={() => {
          setMoveRunDrawerOpen(false);
          onMovedRuns();
          trackMoveRunsWithLocation('Moved runs');
        }}
        open={moveRunDrawerOpen}
        projectName={projectName}
        groupSelectionsRef={groupSelectionsRef}
        selectedCount={selectedCount}
      />
      <Button
        variant="secondary"
        startIcon="forward-next"
        aria-label="Move to group"
        onPress={() => setMoveRunDrawerOpen(true)}>
        Move to group
      </Button>
    </>
  );
}
